const columnOrdersAreEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

export const getColumnGroupKey = column =>
  column?.columnGroupKey || column?.key;

export const getColumnGroupOrder = (columns = []) => {
  const seenGroupKeys = new Set();

  return columns.reduce((groupKeys, column) => {
    const groupKey = getColumnGroupKey(column);
    if (!groupKey || seenGroupKeys.has(groupKey)) return groupKeys;
    seenGroupKeys.add(groupKey);
    groupKeys.push(groupKey);
    return groupKeys;
  }, []);
};

export const expandColumnGroupOrder = (groupOrder = [], columns = []) => {
  const columnsByGroupKey = columns.reduce((groups, column) => {
    const groupKey = getColumnGroupKey(column);
    const groupColumns = groups.get(groupKey) || [];
    groupColumns.push(column);
    groups.set(groupKey, groupColumns);
    return groups;
  }, new Map());
  const normalizedGroupOrder = groupOrder.filter(
    (groupKey, index, keys) =>
      columnsByGroupKey.has(groupKey) && keys.indexOf(groupKey) === index
  );

  getColumnGroupOrder(columns).forEach(groupKey => {
    if (!normalizedGroupOrder.includes(groupKey)) {
      normalizedGroupOrder.push(groupKey);
    }
  });

  return normalizedGroupOrder.flatMap(
    groupKey => columnsByGroupKey.get(groupKey) || []
  );
};

export const moveColumnKeyToIndex = (columnKeys, sourceKey, targetIndex) => {
  if (!sourceKey || !columnKeys.includes(sourceKey)) return columnKeys;

  const nextColumnKeys = columnKeys.filter(key => key !== sourceKey);
  const normalizedTargetIndex = Math.min(
    Math.max(0, targetIndex),
    nextColumnKeys.length
  );
  nextColumnKeys.splice(normalizedTargetIndex, 0, sourceKey);

  return columnOrdersAreEqual(columnKeys, nextColumnKeys)
    ? columnKeys
    : nextColumnKeys;
};

export const moveColumnKey = (
  columnKeys,
  sourceKey,
  destinationKey,
  insertAfter
) => {
  if (!sourceKey || !destinationKey || sourceKey === destinationKey) {
    return columnKeys;
  }

  if (!columnKeys.includes(sourceKey) || !columnKeys.includes(destinationKey)) {
    return columnKeys;
  }

  const destinationIndex = columnKeys
    .filter(key => key !== sourceKey)
    .indexOf(destinationKey);
  return moveColumnKeyToIndex(
    columnKeys,
    sourceKey,
    destinationIndex + (insertAfter ? 1 : 0)
  );
};

export const getColumnInsertionIndex = (
  columnKeys,
  sourceKey,
  headerRectsByKey,
  clientX
) => {
  const remainingColumnKeys = columnKeys.filter(key => key !== sourceKey);

  return remainingColumnKeys.reduce((targetIndex, columnKey) => {
    const rect = headerRectsByKey?.[columnKey];
    if (!rect) return targetIndex;
    return clientX > rect.left + rect.width / 2 ? targetIndex + 1 : targetIndex;
  }, 0);
};
